import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
// Import the plain select directly: the `multichoice` barrel pulls in the
// create-level/user/company wizards (and through them the data-grid column
// factories), which breaks module init order (TDZ) when loaded from here.
import { MemoMultiSelect } from 'components/common/simpleInputFields/multichoice/multiSelect';
import { formatTranslation } from 'intl';
import { InspectionTemplateTranslationKeys, InspectionTranslationKeys } from 'intl/keys';
import { memo, ReactElement, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { GeneralNormConfig } from 'shared/domain/template/generalNormConfig';
import { LabelledEntity } from 'shared/types/commonView';

type GeneralNormConfigField = keyof GeneralNormConfig;

const TOGGLEABLE_FIELDS = [
  { field: 'isControlledPartyVisible', labelKey: InspectionTranslationKeys.controlledParty },
  { field: 'isContractNumberVisible', labelKey: InspectionTranslationKeys.contractNumber },
  { field: 'isWorkTypeVisible', labelKey: InspectionTranslationKeys.columnLabelWorkTypes },
  { field: 'isCommentVisible', labelKey: InspectionTranslationKeys.comment },
  { field: 'isInspectionResultVisible', labelKey: InspectionTranslationKeys.columnLabelResult },
] as const;

const MULTISELECT_ID = 'template-wizard-visible-fields';

const rootSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  width: '100%',
} satisfies SxProps<Theme>;

interface VisibleFieldsSelectProps {
  generalNormConfig: GeneralNormConfig;
  handleGeneralNormConfigChange: <K extends GeneralNormConfigField>(
    field: K,
    value: GeneralNormConfig[K]
  ) => void;
}

/** Configurable inspection header fields shown for this template. */
function VisibleFieldsSelect({
  generalNormConfig,
  handleGeneralNormConfigChange,
}: VisibleFieldsSelectProps): ReactElement {
  const intl = useIntl();

  const options: LabelledEntity[] = useMemo(
    () =>
      TOGGLEABLE_FIELDS.map(({ field, labelKey }) => ({
        _id: field,
        label: formatTranslation(intl, labelKey),
      })),
    [intl]
  );

  const value = useMemo(
    () =>
      options.filter(
        (option) => generalNormConfig[option._id as GeneralNormConfigField]
      ),
    [options, generalNormConfig]
  );

  const handleChange = useCallback(
    (_: unknown, selected: LabelledEntity[]) => {
      const selectedIds = new Set(selected.map((option) => option._id));
      TOGGLEABLE_FIELDS.forEach(({ field }) => {
        const next = selectedIds.has(field);
        if (next !== generalNormConfig[field]) {
          handleGeneralNormConfigChange(field, next);
        }
      });
    },
    [generalNormConfig, handleGeneralNormConfigChange]
  );

  return (
    <Box sx={rootSx}>
      <Box data-qa={MULTISELECT_ID}>
        <MemoMultiSelect
          id={MULTISELECT_ID}
          labelId={`${MULTISELECT_ID}-label`}
          localisedLabel={formatTranslation(
            intl,
            InspectionTemplateTranslationKeys.wizardVisibleFields
          )}
          available={options}
          value={value}
          handleChange={handleChange}
          required={false}
          dense={true}
          reserveSpaceForHelperText={false}
        />
      </Box>
    </Box>
  );
}

export const MemoVisibleFieldsSelect = memo(VisibleFieldsSelect);
